import React, { useCallback, useEffect, useMemo } from "react";
import { Alert, Button, Card } from "react-bootstrap";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormattedMessage } from "react-intl";
import { withAuthorizationVerifier } from "../../components/authorization-verifier";
import { FullWidthLoader } from "../../components/full-width-loader";
import { SectionHeading } from "../../components/section-heading";
import { useUserPropertyTickers } from "../../components/user-tickers-providers";
import { useHttpClient } from "../../lib/http";
import { useConfig } from "../../lib/config";
import { getPropertyRpt } from "../../lib/auth";
import { useKeycloakAuthz } from "../../components/keycloak-provider";
import { useLoyaltyBrand } from "../../components/loyalty-brand-provider";
import { LoyaltyBrandEmailAddressType } from "../../gen/witbooking/backoffice/loyalty/brand/v1/loyalty_brand_api_pb";
import { regenPropertyCache } from "../common/api";
import { useBrandData } from "./hooks/useBrandData";
import { useBrandSave } from "./hooks/useBrandSave";
import { BrandFormSchema } from "./brandFormSchema";
import { hasCompleteWidgetConfiguration, mapWidgetConfigurationToFormData } from "./mappers";
import { messages } from "./messages";
import { BrandFormFields } from "./renders/BrandFormFields";
import { ButtonContainer } from "./styles";
import { BrandFormData, BrandFormInput } from "./types";

const defaultFormValues: BrandFormData = {
  emailReceiver: "",
  emailSender: "no-reply@witbooking.com",
  replyToEmail: "",
  communicationPolicy: "",
  privacyPolicy: "",
  widgetLoyalty: false,
  widgetUrl: "",
  idTierAgency: "",
  idTierCompany: "",
  enableEmailChange: false,
  sendWelcomeEmail: false,
  useExternalTier: false,
  useExternalAuthSystem: false,
  translationsList: [],
  managedByOptions: [],
  usedByOptions: [],
};

function BrandManagerContent() {
  const userPropertyTickersList = useUserPropertyTickers();
  const propertyBrand = useLoyaltyBrand();
  const config = useConfig();
  const httpClient = useHttpClient(config.apiEndpoint);
  const keycloakAuthz = useKeycloakAuthz();

  const methods = useForm<BrandFormInput, unknown, BrandFormData>({
    resolver: zodResolver(BrandFormSchema),
    defaultValues: defaultFormValues,
    mode: "onSubmit",
  });

  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = methods;

  const widgetLoyaltyWatch = watch("widgetLoyalty");

  const {
    selectedBrand,
    loading: loadingData,
    error: dataError,
    managedByOptions,
    usedByOptions,
  } = useBrandData({
    brandTickerOption: propertyBrand
      ? { value: propertyBrand.ticker, label: propertyBrand.ticker }
      : null,
  });

  // State to store backend translations for BrandTranslationComponent
  const [backendTranslations, setBackendTranslations] = React.useState<
    Array<{ locale: string; name: string; description: string }>
  >([]);

  // Reset backendTranslations when brand changes
  useEffect(() => {
    if (!selectedBrand) {
      setBackendTranslations([]);
    }
  }, [selectedBrand]);

  // Reset form when selectedBrand changes
  useEffect(() => {
    if (!propertyBrand) {
      reset(defaultFormValues);
      return;
    }

    if (selectedBrand) {
      // Get email addresses
      const emailAddresses = selectedBrand.getEmailAddressesList();
      const { receiverEmail, senderEmail } = emailAddresses.reduce<{
        receiverEmail: ReturnType<typeof selectedBrand.getEmailAddressesList>[0] | null;
        senderEmail: ReturnType<typeof selectedBrand.getEmailAddressesList>[0] | null;
      }>(
        (acc, email) => {
          if (email.getType() === LoyaltyBrandEmailAddressType.RECEIVER) {
            acc.receiverEmail = email;
          } else if (email.getType() === LoyaltyBrandEmailAddressType.SENDER) {
            acc.senderEmail = email;
          }
          return acc;
        },
        { receiverEmail: null, senderEmail: null }
      );

      const translations = selectedBrand.getTranslationsList();

      // Deduplicate translations by locale (keep last occurrence)
      const translationsMap = new Map<
        string,
        { locale: string; name: string; description: string }
      >();
      translations.forEach(trans => {
        const locale = trans.getLocale();
        translationsMap.set(locale, {
          locale: locale,
          name: trans.getName(),
          description: trans.getDescription() || "",
        });
      });

      const translationsListForForm = Array.from(translationsMap.values());

      const config = selectedBrand.getConfiguration();

      // Widget configuration
      const widgetConfig = selectedBrand.getWidgetConfiguration();
      const hasWidgetConfig = hasCompleteWidgetConfiguration(widgetConfig);
      const widgetFormData = mapWidgetConfigurationToFormData(widgetConfig);

      // NOTE: translationsList is not set here — it is initialized by BrandTranslationComponent
      // via the defaultValues prop to ensure correct ordering by locale.
      reset({
        emailReceiver: receiverEmail?.getValue() || "",
        emailSender: senderEmail?.getValue() || "no-reply@witbooking.com",
        replyToEmail: "",
        communicationPolicy: selectedBrand.getCommunicationpolicyurl() || "",
        privacyPolicy: selectedBrand.getPrivacypolicyurl() || "",
        widgetLoyalty: hasWidgetConfig,
        widgetUrl: widgetFormData.widgetUrl,
        idTierAgency: widgetFormData.idTierAgency,
        idTierCompany: widgetFormData.idTierCompany,
        enableEmailChange: config?.getEnableemailchange() || false,
        sendWelcomeEmail: config?.getSendwelcomeemail() || false,
        useExternalTier: config?.getUseexternaltier() || false,
        useExternalAuthSystem: config?.getUseexternalauthsystem() || false,
        managedByOptions,
        usedByOptions,
      });

      setBackendTranslations(translationsListForForm);
    }
  }, [selectedBrand, propertyBrand, reset, managedByOptions, usedByOptions]);

  const regenCache = useCallback(
    async (usedByList: string[], managedByList: string[]) => {
      if (!selectedBrand || !keycloakAuthz) return;

      try {
        const allProperties = [...new Set([...usedByList, ...managedByList])];

        if (allProperties.length === 0) {
          return;
        }

        const regenPromises = allProperties.map(async propertyTicker => {
          try {
            const propertyToken = await getPropertyRpt(propertyTicker, keycloakAuthz);
            await regenPropertyCache(httpClient, propertyTicker, true, propertyToken);
          } catch (err) {
            console.error(`Failed to regenerate cache for property ${propertyTicker}:`, err);
          }
        });

        await Promise.all(regenPromises);
      } catch (error) {
        console.error("Error regenerating loyalty brand cache:", error);
      }
    },
    [httpClient, selectedBrand, keycloakAuthz]
  );

  const { loading: savingData, error: saveError, saveSuccess, onSubmit } = useBrandSave({
    selectedBrand,
    onSuccess: regenCache,
  });

  const handleFormSubmit = useCallback(
    (data: BrandFormData) => {
      return onSubmit(data);
    },
    [onSubmit]
  );

  const propertyOptions = useMemo(
    () => userPropertyTickersList.map(ticker => ({ value: ticker, label: ticker })),
    [userPropertyTickersList]
  );

  const error = dataError || saveError;

  if (propertyBrand === undefined || loadingData) {
    return <FullWidthLoader />;
  }

  if (propertyBrand === null) {
    return (
      <>
        <SectionHeading>
          <FormattedMessage {...messages.title} />
        </SectionHeading>
        <Card className="ml-4 mr-4 mb-4 mt-4">
          <Alert variant="warning" className="mx-3 mt-3">
            <FormattedMessage
              id="brandManager.noBrandAssociated"
              defaultMessage="No hay ninguna marca asociada a esta propiedad"
            />
          </Alert>
        </Card>
      </>
    );
  }

  if (dataError && !selectedBrand) {
    return (
      <>
        <SectionHeading>
          <FormattedMessage {...messages.title} />
        </SectionHeading>
        <Card className="ml-4 mr-4 mb-4 mt-4">
          <Alert variant="danger" className="mx-3 mt-3">
            <strong>Error al cargar la marca</strong>
            {dataError.message && <div>{dataError.message}</div>}
          </Alert>
        </Card>
      </>
    );
  }

  return (
    <>
      <SectionHeading>
        <FormattedMessage {...messages.title} />
      </SectionHeading>
      <Card className="ml-4 mr-4 mb-4 mt-4">
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(handleFormSubmit)}>
            <BrandFormFields
              control={control}
              errors={errors}
              selectedBrand={selectedBrand}
              propertyBrand={propertyBrand}
              propertyOptions={propertyOptions}
              widgetLoyaltyWatch={widgetLoyaltyWatch}
              backendTranslations={backendTranslations}
            />

            {error && (
              <Alert variant="danger" className="mx-3">
                <strong>
                  <FormattedMessage {...messages.saveError} />
                </strong>
                {error.message && <div>{error.message}</div>}
              </Alert>
            )}

            {saveSuccess && (
              <Alert variant="success" className="mx-3">
                <strong>
                  <FormattedMessage {...messages.saveSuccess} />
                </strong>
              </Alert>
            )}

            {selectedBrand && (
              <ButtonContainer>
                <Button variant="primary" type="submit" disabled={loadingData || savingData}>
                  <FormattedMessage {...messages.save} />
                </Button>
              </ButtonContainer>
            )}
          </form>
        </FormProvider>
      </Card>
    </>
  );
}

export default withAuthorizationVerifier(BrandManagerContent);
